import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Close as CloseIcon } from "../icons";
import { DragIndicator as DragIndicatorIcon } from "../icons";
import { Star as StarIcon } from "../icons";
import { StarBorder as StarBorderIcon } from "../icons";
import { T } from "../routes/Translate";
import { brandColors } from "../theme";
import { removeMediaButtonSx, removeMediaIconSx } from "../routes/mediaFormStyles";

const moveItem = (list, from, to) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const glassButtonSx = {
  ...removeMediaButtonSx,
  top: "auto",
  right: "auto",
};

// Miniaturas de las fotos de un formulario (autos, propiedades) que se
// pueden reordenar: la primera es la portada que se ve en los listados.
// Se arrastra desde el ícono de la esquina (funciona con mouse y con el
// dedo, porque usa pointer events en vez del drag & drop de HTML que no
// existe en mobile) o se toca la estrella para pasarla a portada.
const SortableImageGrid = ({ images = [], onChange, onRemove, hasError = false }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const dragIndexRef = useRef(null);
  const imagesRef = useRef(images);
  imagesRef.current = images;

  useEffect(() => {
    if (dragIndex === null) return undefined;

    const handleMove = (e) => {
      const tile = document.elementFromPoint(e.clientX, e.clientY)?.closest("[data-sortable-image]");
      if (!tile) return;
      const over = Number(tile.dataset.sortableImage);
      const from = dragIndexRef.current;
      if (Number.isNaN(over) || over === from) return;
      onChange(moveItem(imagesRef.current, from, over));
      dragIndexRef.current = over;
      setDragIndex(over);
    };
    const handleUp = () => {
      dragIndexRef.current = null;
      setDragIndex(null);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [dragIndex, onChange]);

  if (!images.length) return null;

  const startDrag = (index) => (e) => {
    e.preventDefault();
    dragIndexRef.current = index;
    setDragIndex(index);
  };

  // Las URLs pueden repetirse si se sube dos veces la misma foto.
  const seen = {};
  const keys = images.map((url) => {
    seen[url] = (seen[url] || 0) + 1;
    return `${url}#${seen[url]}`;
  });

  return (
    <Box sx={{ mt: 2 }}>
      {images.length > 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          <T>Arrastrá las fotos para ordenarlas. La primera es la portada.</T>
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
          gap: 1.25,
          userSelect: dragIndex !== null ? "none" : "auto",
        }}
      >
        {images.map((url, index) => {
          const isCover = index === 0;
          const isDragging = index === dragIndex;
          return (
            <Box
              key={keys[index]}
              data-sortable-image={index}
              sx={{
                position: "relative",
                aspectRatio: "1 / 1",
                borderRadius: 3,
                overflow: "hidden",
                backgroundColor: "action.hover",
                border: "2px solid",
                borderColor: hasError ? "error.main" : isCover ? brandColors.accent : "transparent",
                boxShadow: isDragging ? "0 10px 24px rgba(0,0,0,0.35)" : "0 1px 4px rgba(0,0,0,0.18)",
                transform: isDragging ? "scale(1.05)" : "none",
                opacity: isDragging ? 0.9 : 1,
                zIndex: isDragging ? 2 : 1,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
              }}
            >
              <Box
                component="img"
                src={url}
                alt={`Imagen ${index + 1}`}
                draggable={false}
                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none" }}
              />

              {isCover && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 6,
                    bottom: 6,
                    px: 1,
                    py: 0.25,
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#fff",
                    backgroundColor: "rgba(20,20,20,0.6)",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <T>Portada</T>
                </Box>
              )}

              {images.length > 1 && (
                <IconButton
                  aria-label="Arrastrar para ordenar"
                  onPointerDown={startDrag(index)}
                  sx={{ ...glassButtonSx, top: "6px", left: "6px", cursor: isDragging ? "grabbing" : "grab", touchAction: "none" }}
                >
                  <DragIndicatorIcon sx={removeMediaIconSx} />
                </IconButton>
              )}

              {!isCover && (
                <Tooltip title={<T>Usar como portada</T>}>
                  <IconButton
                    aria-label="Usar como portada"
                    onClick={() => onChange(moveItem(images, index, 0))}
                    sx={{ ...glassButtonSx, bottom: "6px", left: "6px" }}
                  >
                    <StarBorderIcon sx={removeMediaIconSx} />
                  </IconButton>
                </Tooltip>
              )}
              {isCover && images.length > 1 && (
                <StarIcon sx={{ position: "absolute", bottom: 8, right: 8, fontSize: 18, color: brandColors.accent }} />
              )}

              <IconButton aria-label="Eliminar imagen" sx={removeMediaButtonSx} onClick={() => onRemove(index)}>
                <CloseIcon sx={removeMediaIconSx} />
              </IconButton>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SortableImageGrid;
